import React from 'react';
import {
    capitaliseFirst,
    convertHtmlToPlainText,
    numberToWords,
    toCurrencyString,
    toEuroString
} from "../../../assets/scripts/string-functions";
import {
    ApplicationTypes,
    BuildType,
    EmploymentBases,
    EmploymentTypes,
    MortgageTypes,
    PersonalAccommodationSituations
} from "../../../models";
import dayjs from "dayjs";
import {
    balanceOfFundsCosts,
    balanceOfFundsFunds
} from "../../../app/components/client/application-stages/_common/funding/BalanceOfFunds";
import countries from "../../../assets/scripts/countries.json";
import {legacyIncome} from "../../../app/components/client/application-stages/_common/lender-tests/useXlsTests";
import {verifiedInvestments} from "../../../app/components/client/application-stages/_common/funding/verifiedAccounts";
import {camelToWords} from "../../../assets/scripts/functions";
import useSussdPdfObjects from "../../../app/providers/useSussdPdfObjects";

const accountNoteColors = ["pink", "cyan", "orange", "purple"]

const variableIncomeNames = ['shiftAllowance', 'carAllowance', 'otherAllowance', 'overtime', 'bonus', 'commission']

const applicationTypeLabels = {
    [ApplicationTypes.FIRSTTIME]: 'First Time Buyer',
    [ApplicationTypes.MOVINGHOME]: 'Moving Home',
    [ApplicationTypes.SECONDTIME]: 'Second Time Buyer',
    [ApplicationTypes.LANDLORD]: 'Buy To Let',
}

const homeStatusLabels = {
    [PersonalAccommodationSituations.RENTED]: 'Renting',
    [PersonalAccommodationSituations.RELATIVES]: 'Lives with relatives',
    [PersonalAccommodationSituations.OWNED]: 'Owns home',
}

const employmentBasisLabels = {
    [EmploymentBases.FULLTIME]: 'Full time employed',
    [EmploymentBases.PARTTIME]: 'Part time employed',
    [EmploymentBases.CONTRACT]: 'Contract employed',
    [EmploymentBases.TEMPFULLTIME]: 'Temporary Full-time Employment',
    [EmploymentBases.TEMPPARTTIME]: 'Temporary Part-time Employment',
}

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

const parseJson = (value) => typeof value === 'string' ? JSON.parse(value) : value

const parseTestFigures = (mortgage) => {
    const test = mortgage.activeSubmission?.verifiedFiguresTest?.test
    return parseJson(test.figures)
}

export const getTestedFigures = (mortgage) => parseTestFigures(mortgage)

export const getTestedOutput = (mortgage) => {
    const test = mortgage.activeSubmission?.verifiedFiguresTest
    return parseJson(test.output)
}

export const useSussdCoverNoteV2 = (mortgage, test, testResult) => {
    return useSussdPdfObjects(SussdCoverNoteV2, coverNoteV2Props(mortgage, test, testResult))
}

// If applicants have been switched around for testing, swap the figures back
const fixFigures = (mortgage, figures) => {
    const figuresName1 = figures.applicant1?.firstName?.toLowerCase()
    if (mortgage.twoApplicants && figuresName1 !== mortgage.applicant1.firstName.toLowerCase() && figuresName1 === mortgage.applicant2.firstName.toLowerCase()) {
        return {
            ...figures,
            applicant1: figures.applicant2,
            applicant2: figures.applicant1
        }
    }
    return figures
}

const findCoverLetterParagraph = (mortgage, paragraph) => {
    return mortgage.docContents.find(it => it.document === 'coverLetter' && it.paragraph === paragraph)
}

// Plain text of a non-empty cover letter paragraph, or null
const coverLetterText = (mortgage, paragraph) => {
    const existing = findCoverLetterParagraph(mortgage, paragraph)
    return existing && existing.html !== "" ? convertHtmlToPlainText(existing.html) : null
}

const applicantsOf = (mortgage) => mortgage.twoApplicants ? [mortgage.applicant1, mortgage.applicant2] : [mortgage.applicant1]

const hasVariableIncome = (jobs) => variableIncomeNames.some(name => jobs.some(job => {
    const v = job?.[name]
    if (!v) return false
    return (v.guaranteed && v.yearMostRecent > 0) ||
        (!v.guaranteed && ((v.yearMostRecent || 0) + (v.yearBefore || 0) + (v.yearBeforeThat || 0)) > 0)
}))

const variableIncomeTable = (claimedJobs, testedJobs) => {
    const claimed = hasVariableIncome(claimedJobs)
    const tested = hasVariableIncome(testedJobs)
    if (!claimed) {
        return {status: "none", rows: [{label: "None"}]}
    }
    if (!tested) {
        return {status: "not-utilised", rows: [{label: "Not utilised"}]}
    }

    const guaranteedRows = []
    const variableRows = []
    variableIncomeNames.forEach(name => {
        testedJobs.forEach(job => {
            const v = job?.[name]
            if (!v) return
            const labelBase = camelToWords(name)
            if (v.guaranteed) {
                if (v.yearMostRecent > 0) {
                    guaranteedRows.push({label: `Guaranteed ${labelBase}`, value: toEuroString(v.yearMostRecent)})
                }
                return
            }
            [
                ['yearMostRecent', 'Last Year'],
                ['yearBefore', 'Year Before'],
                ['yearBeforeThat', 'Year Before That'],
            ].forEach(([year, suffix]) => {
                if (v[year] > 0) {
                    variableRows.push({label: `${labelBase} ${suffix}`, value: toEuroString(v[year])})
                }
            })
        })
    })
    return {status: "detailed", rows: [...guaranteedRows, ...variableRows]}
}

const variableIncomeGrid = (table) => {
    if (table.status !== "detailed") {
        return (
            <grid>
                <row>
                    <cell>{table.rows[0].label}</cell>
                </row>
            </grid>
        )
    }
    return (
        <grid padding-left={0} padding-right={0} horizontal-bars="inside" columns={[16, 8]}>
            {table.rows.map((r, i) => (
                <row key={i}>
                    <cell>{r.label}</cell>
                    <cell>
                        <string align="right">{r.value}</string>
                    </cell>
                </row>
            ))}
        </grid>
    )
}

const employmentStatus = (employment) => {
    if (employment.employmentType === EmploymentTypes.SELFEMPLOYED) return 'Self Employed'
    if (employment.employmentType === EmploymentTypes.EMPLOYED) return employmentBasisLabels[employment.employmentBasis]
    return capitaliseFirst(employment.employmentType.toLowerCase())
}

export const getAppStrings = (mortgage, n, figures = null) => {
    figures = fixFigures(mortgage, figures || parseTestFigures(mortgage))

    const applicant = mortgage[`applicant${n}`]
    const country = countries.find(it => it.alpha_2_code === applicant.personal.nationality)
    const nationality = country ? country.nationality : 'NO COUNTRY SPECIFIED!!'
    // Use the job with the highest gross basic income (note: sorts employmentIncome in place)
    const employment = legacyIncome(mortgage)
        ? applicant.employment
        : applicant.employmentIncome.sort((a, b) => a.grossBasic > b.grossBasic ? -1 : a.grossBasic < b.grossBasic ? 1 : 0)[0]
    const isEmployed = [EmploymentTypes.EMPLOYED, EmploymentTypes.SELFEMPLOYED].includes(employment.employmentType)

    const tableData = variableIncomeTable(
        mortgage.calculatorFigures.unverified.figures[`applicant${n}`].jobs,
        figures[`applicant${n}`].jobs
    )

    const variableComments = [
        `app${n}VariableIncome`,
        `app${n}SalaryCertEmploymentStartDateMismatch`,
        `app${n}SalaryCertGrossBasicMismatch`,
    ]
        .map(paragraph => coverLetterText(mortgage, paragraph))
        .filter(text => text !== null)
        .reduce((acc, text) => acc === '' ? text : acc + '\n \n' + text, '')

    return {
        name: applicant.fullName,
        nameAndNationality: `${applicant.fullName} (${nationality} Citizen)`,
        age: figures[`applicant${n}`].age.toString() + ' years',
        maritalStatus: capitaliseFirst((applicant.personal.maritalStatus || '').toLowerCase()),
        homeStatus: mortgage.twoApplicants && mortgage.requirement.cohabiting && n === 2
            ? 'Living together'
            : homeStatusLabels[applicant.personal.accommodationSituation] ?? '???????',
        employment: {
            status: employmentStatus(employment),
            occupation: isEmployed ? employment.positionHeld ?? employment.natureOfBusiness : 'N/A',
            employerName: isEmployed ? employment.currentEmployerName ?? employment.nameOfBusiness : 'N/A',
            startDate: isEmployed && employment.currentEmployerStartDate ? dayjs(employment.currentEmployerStartDate, 'YYYY-MM-DD').format('D MMMM YYYY') : '',
            annualGrossBasic: isEmployed ? toEuroString(figures[`basic${n}`]) : '',
            monthlyGrossBasic: isEmployed ? toEuroString(figures[`basic${n}`] / 12) : '',
            variableDetails: variableIncomeGrid(tableData),
            variableData: tableData,
            variableComments: variableComments === '' ? 'NO COMMENT' : variableComments,
        }
    }
}

export const grossBasicMonthlySalary = (jobs) => {
    return toEuroString(jobs.reduce((acc, item) => acc + ((item?.basic?.yearMostRecent || 0) / 12), 0))
}

export const grossBasicAnnualSalary = (jobs) => {
    return toEuroString(jobs.reduce((acc, item) => acc + (item.basic?.yearMostRecent || 0), 0))
}

// Rows of [costKey, costValue, fundKey, fundValue], padded to equal length and ending in TOTAL
const balanceOfFundsTable = (costRows, fundRows, totalCosts) => {
    const length = Math.max(costRows.length, fundRows.length)
    const pad = (rows) => [
        ...rows,
        ...Array(length - rows.length).fill({key: '', value: ''}),
        {key: 'TOTAL', value: toEuroString(totalCosts)}
    ]
    const costs = pad(costRows)
    return pad(fundRows).map((row, i) => [costs[i].key, costs[i].value, row.key, row.value])
}

const praStrings = (mortgage, figures) => {
    const boardingAllowed = ['haven', 'ics'].includes(mortgage.activeSubmission.lenderCode)
    const existing = findCoverLetterParagraph(mortgage, 'repaymentCapacity')
    const combined = (label) => mortgage.twoApplicants ? `Combined ${label}` : label
    const pra1 = figures.applicant1?.pra
    const pra2 = figures.applicant2?.pra
    const accommodationCosts = (pra) => (boardingAllowed ? pra?.boarding || 0 : 0) + (pra?.rent || 0) + (pra?.mortgage || 0)

    const savingsTotal = (pra1?.savings || 0) + (mortgage.twoApplicants ? (pra2?.savings || 0) : 0)
    let accommodationCostsTotal = accommodationCosts(pra1)
    if (figures.twoApplicants) {
        accommodationCostsTotal += accommodationCosts(pra2)
    }
    const clearingLoansTotal = (pra1?.clearingLoans || 0) + (mortgage.twoApplicants ? pra2?.clearingLoans || 0 : 0)

    const rows = [
        {key: combined('Monthly Savings'), value: savingsTotal},
        {key: combined('Monthly Accommodation'), value: accommodationCostsTotal},
        {key: combined('Monthly Loans Discontinuing'), value: clearingLoansTotal},
    ].filter(row => row.value > 0)
    if (pra1?.other) {
        rows.push({key: `${mortgage.applicant1.firstName}'s ${pra1.otherSource}`, value: pra1.other})
    }
    if (mortgage.twoApplicants && pra2?.other) {
        rows.push({key: `${mortgage.applicant2.firstName}'s ${pra2.otherSource}`, value: pra2.other})
    }
    rows.push({key: 'TOTAL PRA', value: rows.reduce((acc, row) => acc + row.value, 0)})

    //TODO - add in the stressed repayment
    return {
        rows: rows.map(row => ({...row, value: toEuroString(row.value)})),
        comment: existing ? convertHtmlToPlainText(existing.html) : 'NO COMMENT'
    }
}

export const coverNoteStrings = (mortgage, figures = null, overrideTestResult = null) => {
    const testResult = overrideTestResult ?? mortgage.activeSubmission?.verifiedFiguresTest
    const test = mortgage.activeSubmission?.verifiedFiguresTest?.test
    if (!figures) {
        figures = parseJson(test.figures)
    }

    const price = testResult.value ?? test.value
    const loan = testResult.loan ?? test.loan
    const override = {price, loan}
    const {stampDuty, legalFees, totalCosts} = balanceOfFundsCosts(mortgage, override)
    const {
        gifts,
        helpToBuy,
        firstHome,
        requiredSavings,
        bookingDepositPaid,
        contractDepositPaid,
        sellingProperties,
    } = balanceOfFundsFunds(mortgage, override)
    const euroInBank = mortgage.accountSummary().currencyAccounts['€'].totalTotal

    const fundRows = [
        {key: 'Mortgage', value: toEuroString(loan)},
        {key: 'Own Contribution', value: toEuroString(requiredSavings)},
        ...[
            ['Gifts', gifts],
            ['Help to Buy Scheme', helpToBuy],
            ['First Home Scheme', firstHome],
            ['Booking Deposit Paid', bookingDepositPaid],
            ['Contract Deposit Paid', contractDepositPaid],
            ['Sale of Property', sellingProperties],
        ].filter(([, amount]) => !!amount).map(([key, amount]) => ({key, value: toEuroString(amount)}))
    ]
    const costRows = [
        {key: 'Purchase Price', value: toEuroString(price)},
        {key: 'Stamp Duty', value: toEuroString(stampDuty)},
        {key: 'Legal Fees', value: toEuroString(legalFees)},
    ]

    const isPurchase = mortgage.mortgageType === MortgageTypes.PURCHASE
    const property = mortgage.properties[0]
    const isSelfBuild = property?.buildType === BuildType.SELFBUILD
    const dependents = figures.dependents.length
    let childcare = figures.applicant1.monthlyOutgoings?.childcare ?? 0
    if (figures.twoApplicants) {
        childcare += figures.applicant2.monthlyOutgoings?.childcare ?? 0
    }

    const strings = {
        applicationType: !isPurchase
            ? 'Refinance'
            : (applicationTypeLabels[mortgage.applicationType] ?? 'Refinance') + (mortgage.applicationType === ApplicationTypes.FIRSTTIME && isSelfBuild ? '- Self Build' : ''),
        children: {
            numberOfDependentsCombined: dependents,
            totalInWords: capitaliseFirst(numberToWords(dependents)),
            totalChildcareCosts: childcare > 0 ? toEuroString(childcare) + ' per month' : 'None',
            numberOfDependentsLabel: mortgage.twoApplicants ? 'Combined Number of Dependents' : 'Number of Dependents'
        },
        property: {
            description: property?.addressKnown || mortgage.mortgageType === 'REFINANCE' || mortgage.mortgageType === 'TOPUP'
                ? property?.address
                : 'Not yet identified',
            descriptionLabel: !isPurchase ? 'Details of Property' : isSelfBuild ? 'Location of Site' : 'Details of Property to Purchase',
            propertyValue: toEuroString(price),
            propertyValueLabel: isPurchase && isSelfBuild ? 'Estimated Final Property Value' : 'Property Value',
        },
        loan: {
            sought: toEuroString(loan),
            term: (testResult.term ?? test.term) + ' years',
            ltv: Math.round(loan / price * 100) + "%",
            bofBreakdown: {
                fullCost: totalCosts,
                bofNeeded: totalCosts - loan,
                euroInBank,
                fundRows: fundRows.filter(it => it.key !== 'Mortgage')
            },
            bof: balanceOfFundsTable(costRows, fundRows, totalCosts),
        },
        pra: praStrings(mortgage, figures),
        app1: getAppStrings(mortgage, 1, figures),
        recommendation: coverLetterText(mortgage, 'recommendation') ?? "NO COMMENT",
    }
    if (mortgage.twoApplicants) {
        strings.app2 = getAppStrings(mortgage, 2, figures)
    }
    return strings
}

export const accountSummaryToReviewOfAccounts = (accountSummary) => {
    return Object.entries(accountSummary.currencyAccounts).map(([currency, {accounts}]) => {
        let totalBalance = 0
        let totalOpening = 0
        const rows = accounts.map(acc => {
            totalBalance += acc.closingOnDate?.balance || 0
            totalOpening += acc.openingOnDate?.balance || 0
            return {
                ownerName: acc.ownerName,
                institution: acc.institution,
                startDate: acc.openingOnDate?.date?.format('D MMM YYYY'),
                startBalance: toCurrencyString(acc.openingOnDate?.balance || 0, currency),
                endDate: acc.closingOnDate?.date?.format('D MMM YYYY'),
                endBalance: toCurrencyString(acc.closingOnDate?.balance || 0, currency),
                months: acc.closingOnDate?.date?.add(1, 'day')?.diff(acc.openingOnDate?.date, 'months'),
                adjustments: acc.adjustmentRows.map(() => ({
                    rows: acc.adjustmentRows,
                    color: accountNoteColors[Math.floor(Math.random() * accountNoteColors.length)]
                })),
                comments: acc.comments,
                notes: [
                    acc.salaried && 'Salaried a/c',
                    acc.accommodationDD && 'Accom. DD a/c',
                    acc.isNewAccount && 'New Account',
                    acc.isClosedAccount && 'Closed Account',
                ].filter(Boolean).join('\n')
            }
        })
        return {
            title: `Review of ${currency} Accounts`,
            rows,
            totalBalance: toCurrencyString(totalBalance, currency),
            totalOpening: toCurrencyString(totalOpening, currency),
        }
    })
}

// Applicant 1, then applicant 2, then joint accounts; € before other currencies, then alphabetical
const savingsRateGroup = (row) => row.joint ? 3 : row.applicant === 1 ? 1 : row.applicant === 2 ? 2 : 99
const savingsRateRowsSorter = (a, b) => {
    const groupDiff = savingsRateGroup(a) - savingsRateGroup(b)
    if (groupDiff !== 0) return groupDiff
    if ((!a.currency || a.currency === "€") && b.currency !== "€") return -1
    if ((a.currency && a.currency !== "€") && b.currency === "€") return 1
    return (a.currency || '').localeCompare(b.currency || '')
}

const expandAccounts = (accounts) => {
    if (accounts.dataSource.length) {
        accounts.needsExplaining = accounts.dataSource.filter(it => !!it.adjustment)
        accounts.notableItems = accounts.dataSource.filter(it => it?.queries?.some(q => !q.excludedOn))
    }
    return accounts
}

export const coverNoteV2Props = (mortgage, test, testResult) => {
    const apply = mortgage.mortgageUploads.apply
    const accountSummary = mortgage.accountSummary()

    const savingsRateRows = mortgage.mortgageType !== 'PURCHASE' ? [] : Object.values(accountSummary.currencyAccounts)
        .flatMap(a => a.accounts.filter(acc => acc.includeInCalculations))
        .map(acc => {
            const converted = (balance) => acc.convertRate && balance ? Math.round(balance / acc.convertRate) : null
            return {
                applicant: acc.applicant,
                ownerName: acc.ownerName,
                institution: acc.institution,
                convertRate: acc.convertRate,
                openingOnDate: acc.openingOnDate?.balance,
                convertedOpeningOnDate: converted(acc.openingOnDate?.balance),
                closingOnDate: acc.closingOnDate?.balance,
                convertedClosingOnDate: converted(acc.closingOnDate?.balance),
                adjustedDifference: acc.total,
                convertedAdjustedDifference: acc.convertedTotal,
                averageSavings: acc.averageSavings,
                convertedAverageSavings: acc.convertedAverageSavings,
                sixMonthDifference: acc.difference,
                // a zero difference is still a real value, so don't treat it as missing like converted() does
                convertedSixMonthDifference: acc.convertRate && acc.difference != null ? Math.round(acc.difference / acc.convertRate) : null,
                totalAdjustments: acc.adjustments,
                convertedTotalAdjustments: converted(acc.adjustments),
                adjustments: acc.adjustmentRows,
                queries: acc.queries,
                currency: acc.currency,
                joint: acc.joint
            }
        })
        .sort(savingsRateRowsSorter)

    const reviewOfAccounts = accountSummaryToReviewOfAccounts(accountSummary)

    const investments = verifiedInvestments(mortgage, apply.filter(it => it.name.startsWith('assets')))
    const euroInvestments = investments.hasOwnProperty('€') ? expandAccounts(investments['€']) : {dataSource: []}
    const nonEuroInvestments = Object.entries(investments)
        .filter(([currency]) => currency !== '€')
        .map(([currency, item]) => Object.assign(expandAccounts(item), {currency}))

    return {
        mortgage,
        requiredRange: accountSummary.requiredRange,
        reviewOfAccounts,
        savingsRateRows,
        euroInvestments,
        nonEuroInvestments,
        test,
        testResult
    }
}

// ---------------------------------------------------------------------------
// Document building blocks
// ---------------------------------------------------------------------------

const labelValueColumns = [8, 16]
const accountColumns = [5, 6, 2, 3, 3, 6]
const ownerCellPadding = {"padding-left": "6", "padding-right": "6", "padding-top": "3", "padding-bottom": "3"}
const commentCellPadding = {"padding-top": "3", "padding-bottom": "3", "padding-left": "3", "padding-right": "3"}

function Section({title, gapAfter = false, children}) {
    return (
        <group>
            <container bg-color="shaded" padding-x={7} padding-y={4}>
                <string color="green" font-weight="bold">{title}</string>
            </container>
            {children}
            <border height={2}/>
            {gapAfter && <gap size={10}/>}
        </group>
    )
}

function LabelCells({label, value, align}) {
    return (
        <>
            <cell>
                <string color="green">{label}</string>
            </cell>
            <cell>
                <string align={align}>{value}</string>
            </cell>
        </>
    )
}

function LabelRow(props) {
    return (
        <row>
            <LabelCells {...props}/>
        </row>
    )
}

function LabelValueGrid({children}) {
    return (
        <grid vertical-bars="both" horizontal-bars="inside" columns={labelValueColumns}>
            {children}
        </grid>
    )
}

function ApplicantRow({label, apps, noPadding = false}) {
    const props = noPadding ? {"padding-left": 0, "padding-right": 0} : {}
    return (
        <row>
            <cell>
                <string color="green">{label}</string>
            </cell>
            {apps.map(app => <cell {...props}>{app}</cell>)}
        </row>
    )
}

function ApplicantGrid({mortgage, children}) {
    return (
        <grid vertical-bars="both" horizontal-bars="inside" columns={mortgage.twoApplicants ? [8, 8, 8] : labelValueColumns}>
            {children}
        </grid>
    )
}

// ---------------------------------------------------------------------------
// Document sections
// ---------------------------------------------------------------------------

const applicantStrings = (mortgage, strings) => mortgage.twoApplicants ? [strings.app1, strings.app2] : [strings.app1]

function ApplicationDetails({mortgage, strings}) {
    const apps = applicantStrings(mortgage, strings)
    return (
        <ApplicantGrid mortgage={mortgage}>
            <ApplicantRow label="Name" apps={apps.map(app => app.nameAndNationality)}/>
            <ApplicantRow label="Age" apps={apps.map(app => app.age)}/>
            <ApplicantRow label="Marital Status" apps={apps.map(app => app.maritalStatus)}/>
            <ApplicantRow label="Home Status" apps={apps.map(app => app.homeStatus)}/>
        </ApplicantGrid>
    )
}

function DependentDetails({mortgage, dependents}) {
    return (
        <LabelValueGrid>
            <LabelRow label={dependents.numberOfDependentsLabel} value={dependents.totalInWords}/>
            {dependents.numberOfDependentsCombined > 0 && (
                <>
                    <LabelRow label="Childcare Costs" value={dependents.totalChildcareCosts}/>
                    <LabelRow label="Comments" value={coverLetterText(mortgage, 'childcareCosts') ?? 'NO COMMENT'}/>
                </>
            )}
        </LabelValueGrid>
    )
}

function EmploymentDetails({mortgage, strings, figures}) {
    const apps = applicantStrings(mortgage, strings)
    const jobs = mortgage.twoApplicants ? [figures.applicant1.jobs, figures.applicant2.jobs] : [figures.applicant1.jobs]
    return (
        <ApplicantGrid mortgage={mortgage}>
            <ApplicantRow label="Applicant Name" apps={apps.map(app => app.name)}/>
            <ApplicantRow label="Employment Status" apps={apps.map(app => app.employment.status)}/>
            <ApplicantRow label="Occupation" apps={apps.map(app => app.employment.occupation)}/>
            <ApplicantRow label="Employers Name" apps={apps.map(app => app.employment.employerName)}/>
            <ApplicantRow label="Start Date of Employment" apps={apps.map(app => app.employment.startDate)}/>
            <ApplicantRow label="Gross Basic Monthly Salary" apps={jobs.map(grossBasicMonthlySalary)}/>
            <ApplicantRow label="Gross Basic Annual Salary" apps={jobs.map(grossBasicAnnualSalary)}/>
            <ApplicantRow noPadding={true} label="Annual Additional Income" apps={apps.map(app => app.employment.variableDetails)}/>
            <ApplicantRow label="Comments" apps={apps.map(app => app.employment.variableComments)}/>
        </ApplicantGrid>
    )
}

function PropertyDetails({mortgage, strings}) {
    const property = mortgage.properties[0]
    const isSelfBuild = mortgage.mortgageType === MortgageTypes.PURCHASE && property?.buildType === BuildType.SELFBUILD
    const gifterDetails = JSON.parse(mortgage.requirement.gifters || '[]')
    return (
        <LabelValueGrid>
            <LabelRow label={strings.property.descriptionLabel} value={strings.property.description}/>
            {isSelfBuild && (
                <>
                    <LabelRow label="Site Value:" value={toEuroString(property?.siteValue)}/>
                    <LabelRow label="Construction Costs:" value={toEuroString(property?.constructionCosts)}/>
                </>
            )}
            <LabelRow label={strings.property.propertyValueLabel} value={strings.property.propertyValue}/>
            {mortgage.requirement.receivingGifts ? (
                <row>
                    <cell>
                        <string color="green">Gifter Details:</string>
                    </cell>
                    <cell padding-left={0} padding-right={0} padding-top={0} padding-bottom={0}>
                        <grid columns={[4, 4, 6, 10]}>
                            <row>
                                <cell>
                                    <string>{toEuroString(mortgage.requirement.depositGifts)}</string>
                                </cell>
                                <cell>
                                    <string align="right">from:</string>
                                </cell>
                                <cell>
                                    <string>{gifterDetails.map(({fullName, relationship}) => `${fullName} - ${relationship}`).join('\n')}</string>
                                </cell>
                                <cell></cell>
                            </row>
                        </grid>
                    </cell>
                </row>
            ) : null}
            <LabelRow label="Mortgage Amount Sought" value={strings.loan.sought}/>
            <LabelRow label="Term" value={strings.loan.term}/>
            <LabelRow label="LTV" value={strings.loan.ltv}/>
        </LabelValueGrid>
    )
}

function PropertiesBeingSoldGroup({mortgage}) {
    const properties = applicantsOf(mortgage).flatMap(applicant => applicant.properties.filter(it => it.toBeSold))
    if (properties.length === 0) {
        return null
    }
    const rows = [
        ['Address', property => property.address],
        ['Property Type', property => capitaliseFirst(property.propertyType.toLowerCase())],
        ['Estimated value', property => toEuroString(property.estimatedValue)],
        ['Mortgage Outstanding', property => toEuroString(property.outstanding)],
        ['Expected Sale Proceeds', property => toEuroString(property.estimatedValue - (property.outstanding || 0))],
    ]
    return (
        <Section title={(properties.length > 1 ? 'Properties Being Sold' : 'Property Being Sold').toUpperCase()}>
            <grid vertical-bars="both" horizontal-bars="inside" columns={[8, ...properties.map(() => 16 / properties.length)]}>
                {rows.map(([label, value]) => (
                    <row>
                        <cell>
                            <string color="green">{label}</string>
                        </cell>
                        {properties.map(property => (
                            <cell>
                                <string>{value(property)}</string>
                            </cell>
                        ))}
                    </row>
                ))}
            </grid>
        </Section>
    )
}

function BalanceOfFunds({rows}) {
    return (
        <grid vertical-bars="both" horizontal-bars="inside" columns={[8, 4, 8, 4]}>
            {rows.map(row => (
                <row>
                    <LabelCells label={row[0]} value={row[1]} align="right"/>
                    <LabelCells label={row[2]} value={row[3]} align="right"/>
                </row>
            ))}
        </grid>
    )
}

// A savings amount in €, with the original currency amount underneath for converted accounts
function SavingsAmount({row, converted, original}) {
    if (!row.convertRate) {
        return <string align="right">{original?.toEuro(0)}</string>
    }
    return (
        <>
            <string align="right">{converted?.toEuro(0)}</string>
            <string align="right" font-size="6" color="muted">{row.currency}{original?.toCurrency(0)}</string>
        </>
    )
}

function SavingsRateHeading({align, children}) {
    return (
        <cell>
            <string align={align} color="green">{children}</string>
        </cell>
    )
}

function SavingsRateWithAdjustments({savingsRateRows, requiredRange, figures, strings}) {
    const hasRows = savingsRateRows.length > 0
    const anyHaveAdjustments = savingsRateRows.some(row => Math.abs(row.totalAdjustments) > 0)
    const totalSavings = savingsRateRows.reduce((acc, row) => acc + (row.convertedAverageSavings ?? row.averageSavings ?? 0), 0)
    const columns = anyHaveAdjustments ? [2.5, 3.5, 3, 3, 3, 3, 3, 3] : [4, 4, 4, 4, 4, 4]
    const figuresAmount = (figures.applicant1.pra?.savings || 0) + (figures?.applicant2?.pra?.savings || 0)
    const warningMessage = Math.abs(Number(totalSavings) - Number(figuresAmount)) > 2
        ? `Current Savings Rate (${totalSavings.toEuro(0)}) does not match LenderTest Savings Rate (${figuresAmount.toEuro(0)})`
        : null

    // First FX rate seen for each non-euro currency
    const fxRates = {}
    for (const row of savingsRateRows) {
        if (row.currency && row.currency !== '€' && !fxRates[row.currency]) {
            fxRates[row.currency] = row.convertRate
        }
    }
    const uniqueFxs = Object.entries(fxRates)

    return (
        <>
            {hasRows && (
                <grid vertical-bars="both" horizontal-bars="both" columns={columns}>
                    <row>
                        <SavingsRateHeading>A/C Holder</SavingsRateHeading>
                        <SavingsRateHeading>Institution</SavingsRateHeading>
                        <SavingsRateHeading align="right">{requiredRange.start.format('D MMM')} Opening</SavingsRateHeading>
                        <SavingsRateHeading align="right">{requiredRange.end.format('D MMM')} Closing</SavingsRateHeading>
                        <SavingsRateHeading align="right">Difference</SavingsRateHeading>
                        {anyHaveAdjustments && (
                            <>
                                <SavingsRateHeading align="right">Adjustments</SavingsRateHeading>
                                <SavingsRateHeading align="right">Adjusted Diff</SavingsRateHeading>
                            </>
                        )}
                        <SavingsRateHeading align="right">Savings Rate</SavingsRateHeading>
                    </row>
                    {savingsRateRows.map(row => {
                        const sign = row.totalAdjustments > 0 ? '+' : '-'
                        return (
                            <row>
                                <cell>
                                    <string>{row.ownerName}</string>
                                </cell>
                                <cell>
                                    <string>{row.institution}</string>
                                </cell>
                                <cell>
                                    <SavingsAmount row={row} converted={row.convertedOpeningOnDate} original={row.openingOnDate}/>
                                </cell>
                                <cell>
                                    <SavingsAmount row={row} converted={row.convertedClosingOnDate} original={row.closingOnDate}/>
                                </cell>
                                <cell>
                                    <SavingsAmount row={row} converted={row.convertedSixMonthDifference} original={row.sixMonthDifference}/>
                                </cell>
                                {anyHaveAdjustments && (
                                    <>
                                        <cell>
                                            {row.totalAdjustments !== 0 && (row.convertRate ? (
                                                <>
                                                    <string align="right">{sign} {Math.abs(row.convertedTotalAdjustments)?.toEuro(0)}</string>
                                                    <string align="right" font-size="6" color="muted">{sign} {row.currency}{Math.abs(row.totalAdjustments)?.toCurrency(0)}</string>
                                                </>
                                            ) : (
                                                <string align="right">{sign} {Math.abs(row.totalAdjustments)?.toEuro(0)}</string>
                                            ))}
                                        </cell>
                                        <cell>
                                            <SavingsAmount row={row} converted={row.convertedAdjustedDifference} original={row.adjustedDifference}/>
                                        </cell>
                                    </>
                                )}
                                <cell>
                                    <SavingsAmount row={row} converted={row.convertedAverageSavings} original={row.averageSavings}/>
                                </cell>
                            </row>
                        )
                    })}
                </grid>
            )}
            <grid vertical-bars="outside" horizontal-bars="inside" columns={labelValueColumns}>
                {strings?.pra?.rows.map(row => <LabelRow label={row.key} value={row.value} align="right"/>)}
            </grid>
            {hasRows && anyHaveAdjustments && (
                <>
                    <grid vertical-bars="outside" horizontal-bars="inside" columns={[24]}>
                        <row>
                            <cell>
                                <string color="green">Adjustments Explanation</string>
                            </cell>
                        </row>
                    </grid>
                    <grid vertical-bars="both" horizontal-bars="both" columns={[7, 4, 9, 4]}>
                        {savingsRateRows
                            .flatMap(row => row.adjustments?.length ? [...row.adjustments].sort((a, b) => a.date.diff(b.date)) : [])
                            .map(adjustment => (
                                <row>
                                    <cell>
                                        <string font-size="7">{adjustment.accountTitle}</string>
                                    </cell>
                                    <cell>
                                        <string font-size="7">{adjustment.date.format('D MMM YYYY')}</string>
                                    </cell>
                                    <cell>
                                        <string font-size="7">{adjustment.lenderAnswer.join('\n')}</string>
                                    </cell>
                                    <cell>
                                        <string align="right" font-size="7">{adjustment.direction === "UP" ? '+' : '-'} {(adjustment.amount)?.toEuro(0)}</string>
                                    </cell>
                                </row>
                            ))}
                    </grid>
                </>
            )}
            {hasRows && warningMessage && (
                <grid horizontal-bars="both" columns={[24]}>
                    <row>
                        <cell>
                            <string align="center" color="red">{warningMessage}</string>
                        </cell>
                    </row>
                </grid>
            )}
            <grid vertical-bars="outside" horizontal-bars="outside" columns={[24]}>
                <row>
                    <cell>
                        <string color="green">Comments</string>
                    </cell>
                </row>
                <row>
                    <cell>
                        <string>{strings?.pra?.comment}</string>
                    </cell>
                </row>
                {uniqueFxs.length > 0 && (
                    <row>
                        <cell>
                            <string color="green">FX Conversions used above</string>
                            {uniqueFxs.map(([currency, fxRate]) => (
                                <string key={currency}>€1 = {currency}{fxRate}</string>
                            ))}
                        </cell>
                    </row>
                )}
            </grid>
        </>
    )
}

function OtherPropertiesGroup({mortgage}) {
    const properties = applicantsOf(mortgage).flatMap(applicant => applicant.properties
        .filter(it => !it.toBeSold)
        .map(it => ({...it, owner: applicant.firstName})))
    if (properties.length === 0) {
        return null
    }
    const money = (amount, currency) => currency ? toCurrencyString(amount, currency) : toEuroString(amount)
    const headings = ['Owner', 'Address', 'Type', 'Rental Inc.', 'Repayment', 'Lender', 'Loan O/S', 'Term O/S', 'Value', 'Net Value']
    return (
        <Section title="OTHER PROPERTIES">
            <grid vertical-bars="both" horizontal-bars="inside" columns={[2.4, 3.4, 2.4, 2.4, 2.4, 2.4, 2.4, 1.4, 2.4, 2.4]}>
                <row>
                    {headings.map(heading => (
                        <cell>
                            <string color="green">{heading}</string>
                        </cell>
                    ))}
                </row>
                {properties.map(property => {
                    const {currency, isMortgaged} = property
                    return (
                        <row>
                            <cell>
                                <string>{property.owner}</string>
                            </cell>
                            <cell>
                                <string>{property.address}</string>
                            </cell>
                            <cell>
                                <string>{capitaliseFirst(property.propertyType.toLowerCase())}</string>
                            </cell>
                            <cell>
                                <string align="right">{property.residingHere ? "PDH" : property.isRented ? money(property.rentPerMonth, currency) : 'Not Rented'}</string>
                            </cell>
                            <cell>
                                <string align="right">{isMortgaged ? money(property.monthlyRepayments, currency) : 'No Mortgage'}</string>
                            </cell>
                            <cell>
                                <string>{isMortgaged ? property.lender : ''}</string>
                            </cell>
                            <cell>
                                <string align="right">{isMortgaged ? toCurrencyString(property.outstanding, currency || '€') : ''}</string>
                            </cell>
                            <cell>
                                <string align="right">{isMortgaged ? property.remainingTerm + 'yr' : ''}</string>
                            </cell>
                            <cell>
                                <string align="right">{money(property.estimatedValue, currency)}</string>
                            </cell>
                            <cell>
                                <string align="right">{money(property.estimatedValue - property.outstanding, currency)}</string>
                            </cell>
                        </row>
                    )
                })}
            </grid>
        </Section>
    )
}

// Owner cell plus the remaining account columns in a nested grid
function AccountRow({owner, children}) {
    return (
        <row>
            {owner}
            <cell>
                <grid vertical-bars="both" horizontal-bars="none" columns={accountColumns}>
                    <row>
                        {children}
                    </row>
                </grid>
            </cell>
        </row>
    )
}

function AccountsReview({item}) {
    const headings = [['Institution'], ['Date Range'], ['Mths'], ['Opening Balance', 'right'], ['Closing Balance', 'right'], ['Comment']]
    const rowsWithComments = item.rows.filter(row => row.comments.length > 0)
    return (
        <grid columns={[3, 21]} vertical-bars="outside" horizontal-bars="both" cell-padding-x="0" cell-padding-y="0">
            <AccountRow owner={<cell {...ownerCellPadding}><string color="green">A/c Holder</string></cell>}>
                {headings.map(([heading, align]) => (
                    <cell>
                        <string color="green" align={align}>{heading}</string>
                    </cell>
                ))}
            </AccountRow>

            {item.rows.map((row, index) => (
                <AccountRow key={index} owner={<cell {...ownerCellPadding}><string>{row.ownerName}</string></cell>}>
                    <cell>
                        <string>{row.institution}</string>
                    </cell>
                    <cell>
                        <string>{row.startDate} - {row.endDate}</string>
                    </cell>
                    <cell>
                        <string>{`${row.months || '-'}`}</string>
                    </cell>
                    <cell>
                        <string align="right">{row.startBalance}</string>
                    </cell>
                    <cell>
                        <string align="right">{row.endBalance}</string>
                    </cell>
                    <cell>
                        <string>{row.notes}</string>
                    </cell>
                </AccountRow>
            ))}

            <AccountRow owner={<cell></cell>}>
                <cell></cell>
                <cell></cell>
                <cell>
                    <string color="green" align="right">TOTALS</string>
                </cell>
                <cell>
                    <string align="right">{item.totalOpening}</string>
                </cell>
                <cell>
                    <string align="right">{item.totalBalance}</string>
                </cell>
                <cell></cell>
            </AccountRow>

            <row>
                <cell {...ownerCellPadding}>
                    <string color="green">Comments</string>
                </cell>
                <cell>
                    {rowsWithComments.length === 0 && (
                        <grid vertical-bars="both" horizontal-bars="none" columns={[24]}>
                            <row>
                                <cell>
                                    <bullet>No notable items</bullet>
                                </cell>
                            </row>
                        </grid>
                    )}
                    <grid vertical-bars="none" horizontal-bars="none" columns={[5, 19]} cell-padding-y="0">
                        {rowsWithComments.map(row => (
                            <row>
                                <cell {...commentCellPadding}>
                                    <string font-size="8" font-weight="bold">{row.ownerName} - {row.institution}</string>
                                </cell>
                                <cell {...commentCellPadding}>
                                    <grid vertical-bars="none" horizontal-bars="none" columns={[8, 4, 12]} cell-padding-y="0">
                                        {row.comments.map(comment => (
                                            <row>
                                                <cell>
                                                    <bullet bullet-size={0.6} bullet-color="green">
                                                        <string font-size="7" color="muted">{comment.title}</string>
                                                    </bullet>
                                                </cell>
                                                <cell>
                                                    <string font-size="7" color="muted">{comment.date}</string>
                                                </cell>
                                                <cell>
                                                    <string font-size="7" color="muted">{comment.text}</string>
                                                </cell>
                                            </row>
                                        ))}
                                    </grid>
                                </cell>
                            </row>
                        ))}
                    </grid>
                </cell>
            </row>
        </grid>
    )
}

function InvestmentGroup({item}) {
    if (!item.dataSource.length) {
        return null
    }
    if (!item.currency) {
        item.currency = '€'
    }
    let colorIndex = -1
    item.dataSource.forEach(row => {
        if (row.adjustment?.explanation) {
            colorIndex++
            row.adjustment.color = accountNoteColors[colorIndex]
        }
    })
    const dataSource = item.dataSource

    return (
        <Section title={<>OTHER {item.currency} WEALTH</>}>
            <grid vertical-bars="both" horizontal-bars="inside" columns={[3, 18, 3]}>
                <row>
                    <cell>
                        <string color="green">Account Holder</string>
                    </cell>
                    <cell>
                        <string color="green">Institution</string>
                    </cell>
                    <cell>
                        <string color="green">Current Value</string>
                    </cell>
                </row>
                {dataSource.map(row => (
                    <row>
                        <cell>
                            <string>{row.owner}</string>
                        </cell>
                        <cell>
                            <string>{row.account}</string>
                        </cell>
                        <cell>
                            <string align="right">{toCurrencyString(row.closingBalance, item.currency)}</string>
                        </cell>
                        <cell>
                            <grid columns={[2, 22]} cell-padding-x="0" cell-padding-y="0">
                                <row>
                                    <cell>
                                        <sentence>
                                            {!!row.salaried && <string color="blue">*</string>}
                                            {!!row.accommodationDD && <string color="lime">*</string>}
                                            {!!row.adjustment?.explanation && <string color={row.adjustment.color}>*</string>}
                                        </sentence>
                                    </cell>
                                    <cell></cell>
                                </row>
                            </grid>
                        </cell>
                    </row>
                ))}
                <row>
                    <cell></cell>
                    <cell>
                        <string align="right" color="green">TOTALS</string>
                    </cell>
                    <cell>
                        <string align="right">{toCurrencyString(item.totalClosingBalance, item.currency)}</string>
                    </cell>
                </row>
            </grid>
            <grid vertical-bars="both" horizontal-bars="both" columns={[3, 21]}>
                <row>
                    <cell>
                        <string color="green">Comments</string>
                    </cell>
                    <cell>
                        {dataSource.some(it => !!it.salaried) && (
                            <bullet dot="*" color="blue">Salaried Account</bullet>)}
                        {dataSource.some(it => !!it.accommodationDD) && (
                            <bullet dot="*" color="lime">Accommodation Direct Debit</bullet>)}
                        {item.needsExplaining.map(it => (
                            <bullet dot="*" color={it.adjustment.color}>{it.adjustment.explanation}</bullet>
                        ))}
                        {item.notableItems.length > 0 ? (
                            <bullet>
                                <string>Notable items:</string>
                                {item.notableItems.map(notable => (
                                    <>
                                        <string font-weight="bold">{`${notable.owner}'s ${notable.account}`}</string>
                                        <grid columns={[5, 5, 14]} cell-padding-x="0" cell-padding-y="1">
                                            {notable.queries.filter(q => !q.excludedOn).map(query => (
                                                <row>
                                                    <cell>{query.description}</cell>
                                                    <cell>{!!query.date ? dayjs(query.date, "YYYY-MM-DD").format("D MMM YYYY") : ''}</cell>
                                                    <cell>
                                                        {(query.lenderAnswer || []).map(it => <string>{it}</string>)}
                                                    </cell>
                                                </row>
                                            ))}
                                        </grid>
                                    </>
                                ))}
                            </bullet>
                        ) : <bullet>No notable items</bullet>}
                    </cell>
                </row>
            </grid>
        </Section>
    )
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

function SussdCoverNoteV2({
                              mortgage,
                              requiredRange,
                              savingsRateRows,
                              reviewOfAccounts,
                              euroInvestments,
                              nonEuroInvestments,
                              test,
                              testResult,
                          }) {
    const figures = fixFigures(mortgage, parseJson(test.figures))
    const strings = coverNoteStrings(mortgage, figures, testResult)

    return (
        <document height="infinite" group-gap={10}>
            <group>
                <header>Cover Note</header>
                <border color="green" height={4}/>
                <LabelValueGrid>
                    <LabelRow label="Application Type" value={strings.applicationType}/>
                </LabelValueGrid>
                <border height={2}/>
            </group>

            <Section title="CLIENT DETAILS">
                <ApplicationDetails mortgage={mortgage} strings={strings}/>
            </Section>

            <Section title="DEPENDENT DETAILS">
                <DependentDetails mortgage={mortgage} dependents={strings.children}/>
            </Section>

            <Section title="EMPLOYMENT AND INCOME">
                <EmploymentDetails mortgage={mortgage} strings={strings} figures={figures}/>
            </Section>

            <Section title="PROPERTY DETAILS">
                <PropertyDetails mortgage={mortgage} strings={strings}/>
            </Section>

            <PropertiesBeingSoldGroup mortgage={mortgage}/>

            {mortgage.mortgageType === MortgageTypes.PURCHASE && (
                <Section title="BALANCE OF FUNDS">
                    <BalanceOfFunds rows={strings.loan.bof}/>
                </Section>
            )}

            {mortgage.activeSubmission.lenderCode !== "nua" && (<Section title={<>PROVEN REPAYMENT ABILITY {savingsRateRows.length > 0 && '(with avg. savings rate)'}</>}>
                <SavingsRateWithAdjustments
                    savingsRateRows={savingsRateRows}
                    requiredRange={requiredRange}
                    figures={figures}
                    strings={strings}
                />
            </Section>) }


            {mortgage.applicationType !== "LANDLORD" && <OtherPropertiesGroup mortgage={mortgage}/>}

            <Section title="RECOMMENDATION:" gapAfter>
                <grid columns={[24]} vertical-bars="true">
                    <row>
                        <cell>{strings.recommendation}</cell>
                    </row>
                </grid>
            </Section>

            {reviewOfAccounts.map(group => (
                <Section key={group.title} title={group.title.toUpperCase()}>
                    <AccountsReview item={group}/>
                </Section>
            ))}
            <InvestmentGroup item={euroInvestments}/>
            {nonEuroInvestments.map(item => <InvestmentGroup item={item}/>)}
            <gap size={10}/>
        </document>
    )
}

export default SussdCoverNoteV2;
